// Audit table (Analysis > MeshCore > Audit): the payload served by
// GET /api/audit/table and every pure rule the page renders from it --
// filters, sort keys, summary-bar counts, and the hover-tooltip text. This
// file has NO imports on purpose: it is compiled on its own and covered by
// node:test (frontend/tests/auditTable.test.mjs), so each rule that decides
// what the page counts, hides or says lives here, not in JSX.

export type AuditTone = "green" | "yellow" | "red" | "none";
export type AuditWindow = "24h" | "7d";

export const DASH = "—";

export interface AuditCell {
  value: number | string | null;
  label: string | null;
  color: string | null;
  evidence: Record<string, unknown>;
}

export interface AuditScopePill {
  tag: string;
  name: string | null;
  color: "green" | "grey" | "red";
  dashed: boolean;
  confirmed?: boolean;
  /** The node reported this scope but it has not been reported or seen carrying it for a while; still counted. */
  stale?: boolean;
  /** Whole days from the scope's freshness time (latest of declared / seen) to the window end; only on a stale pill. */
  stale_days?: number;
  last_seen?: string;
}

/** The mark for a stale scope: "Stale · last seen 9 days ago". */
export function scopeStaleLabel(days: number): string {
  return `Stale · last seen ${days} ${days === 1 ? "day" : "days"} ago`;
}

export interface ScopeStaleInfo {
  last_seen: string;
  stale_days: number;
}

export type ScopeState = "scoped" | "no_scope" | "unknown";

export interface AuditRegionCell extends AuditCell {
  scope_pills: AuditScopePill[] | null;
  scope_state: ScopeState | null;
}

export interface AuditReliance {
  value: number | null;
  choke: number;
  through: number;
}

export interface AuditWidthChanges {
  widths: number[];
  adverts: number;
  last_change: string | null;
}

export interface AuditDuplicatePartner {
  node_id: string;
  name: string;
  km: number | null;
  heard_together: boolean | null;
  observers: string[];
}

/** Relayed packets in the window by path-hash width. */
export interface AuditPathHashPackets {
  total: number;
  w1: number;
  w2: number;
  w3: number;
}

export interface AuditRow {
  native_id: string;
  name: string;
  short_id: string | null;
  role: string;
  raw_role?: string | null; // the role from the node's own advert; empty = never heard advertising
  region: AuditRegionCell;
  /** Scope Footprint: Consistent / Inconsistent / Unknown, with the scope table in `evidence`. Absent in older snapshots. */
  footprint?: AuditCell;
  path_hash: AuditCell;
  path_width: number | null;
  /** The node's own advert widths in the Audit window when they changed, else null. */
  width_changes?: AuditWidthChanges | null;
  /** Other repeaters / room servers sharing this node's short ID at its own width. */
  duplicates?: AuditDuplicatePartner[];
  /** Km to the closest sharing partner with a known position, else null. */
  duplicates_nearest_km?: number | null;
  multibyte: "yes" | "unknown" | null;
  duplicate: AuditCell;
  clock_offset: AuditCell;
  advert_interval: AuditCell;
  loop_detection: AuditCell;
  last_heard: string | null;
  pkts_per_hr: AuditCell;
  reliance: AuditReliance;
  /** Seconds on air per day over the window; null when nothing is attributed to the node. */
  airtime?: AuditAirtime | null;
  has_top_scope: boolean | null;
  has_issue: boolean;
}

export interface AuditAirtimeType {
  type: string;
  s_per_day: number;
}

export interface AuditAirtime {
  sent: { s_per_day: number; by_type: AuditAirtimeType[]; rank?: number | null; of?: number | null };
  relayed: { s_per_day: number; rank?: number | null; of?: number | null };
  caused: { s_per_day: number; by_type: AuditAirtimeType[]; rank?: number | null; of?: number | null };
  advert: {
    adverts: number; zero_hop: number; flood: number;
    s_per_advert: number | null; repeats_per_advert: number | null;
  } | null;
}

export interface AuditColorSettings {
  pkts_per_hr: { chatty_from: number };
  clock_offset_s: { yellow_from: number; red_from: number };
  advert_interval_hours: { yellow_from: number; green_from: number; neutral_ceiling_hours?: number };
}

export interface AuditPayload {
  window: string;
  generated_at?: string;
  findings_snapshot_at?: string | null;
  window_start?: string;
  window_end?: string;
  rows: AuditRow[];
  node_count: number;
  issue_count: number;
  top_level_region_scope: string | null;
  scope_tokens: string[];
  width_lookback_days: number;
  path_hash_packets?: AuditPathHashPackets;
  fixed_lookback_days: number | null;
  color_settings: AuditColorSettings;
  pending?: boolean;
  message?: string;
}

// ---- roles ------------------------------------------------------------------

export function isInfra(row: AuditRow): boolean {
  return row.role === "repeater" || row.role === "room_server";
}

// ---- population (Location + Role) -------------------------------------------

export function populationFor(rows: AuditRow[], location: string, role: string): AuditRow[] {
  return rows.filter((r) => (!location || r.region.value === location) && (!role || r.role === role));
}

/** The nodes the table and its one total are about: repeaters and room servers,
 * unless "Show all nodes" is on or Role is Companion. */
export function issuePopulation(rows: AuditRow[], location: string, role: string, showAll: boolean): AuditRow[] {
  const pop = populationFor(rows, location, role);
  return showAll || role === "companion" ? pop : pop.filter(isInfra);
}

// ---- column filters ---------------------------------------------------------

export type ColumnFilterId = "scope" | "foot" | "path" | "multi" | "dup" | "clock" | "adv" | "loop" | "pkts";
export type ColumnFilters = Partial<Record<ColumnFilterId, string>>;

export interface FilterContext {
  settings: AuditColorSettings;
}

function scopeTagsOf(row: AuditRow): string[] {
  return (row.region.scope_pills ?? []).filter((p) => p.color !== "red").map((p) => p.tag);
}

export function rowHasScope(row: AuditRow, tag: string): boolean {
  const t = tag.toLowerCase();
  return scopeTagsOf(row).some((x) => x.toLowerCase() === t);
}

/** More than a year off, either way: the clock was never set (it still shows the radio's default). */
export const CLOCK_NOT_SET_ABS_S = 365.25 * 86400;

export function isClockNotSet(value: unknown): boolean {
  return typeof value === "number" && Number.isFinite(value) && Math.abs(value) > CLOCK_NOT_SET_ABS_S;
}

export const CLOCK_NOT_SET_LABEL = "clock not set";

/** The node's 28 h firmware-step level (clock ahead by N x 28 h), from the cell's evidence; null when unknown or off-lattice. */
export function clockStepLevel(cell: AuditCell): number | null {
  const v = cell.evidence?.step_28h_level;
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/** The clock cell as shown: over a year off either way always reads "clock not set" in red,
 * whatever label an older cached payload carried. */
export function clockCellForDisplay(cell: AuditCell): AuditCell {
  return isClockNotSet(cell.value) ? { ...cell, label: CLOCK_NOT_SET_LABEL, color: "red" } : cell;
}

/** A clock reading exists only when the cell has a number AND something to show for it. */
function clockMagnitude(row: AuditRow): number | null {
  const c = row.clock_offset;
  if (typeof c.value !== "number" || !Number.isFinite(c.value)) return null;
  if (c.label === DASH) return null;
  return Math.abs(c.value);
}

function advertHours(row: AuditRow): number | null {
  const v = row.advert_interval.value;
  return typeof v === "number" ? v : null;
}

/** Scope-column filter. `top` is the top-level scope from Settings (may be
 * empty); a value starting with "#" means one specific scope. */
export function matchesScopeFilter(row: AuditRow, value: string, top: string | null): boolean {
  const state = row.region.scope_state;
  if (value.startsWith("#")) return rowHasScope(row, value);
  switch (value) {
    case "has": return state === "scoped";
    case "top": return isInfra(row) && !!top && rowHasScope(row, top);
    case "noTop": return isInfra(row) && !!top && !rowHasScope(row, top) && state !== "unknown" && state !== null;
    case "none": return state === "no_scope";
    case "unknown": return isInfra(row) && (state === "unknown" || state === null);
    default: return true;
  }
}

export function matchesColumnFilter(
  row: AuditRow, id: ColumnFilterId, value: string, ctx: FilterContext, top: string | null,
): boolean {
  if (!value) return true;
  const s = ctx.settings;
  switch (id) {
    case "scope": return matchesScopeFilter(row, value, top);
    case "foot": return isInfra(row) && footprintValue(row) === value;
    case "path":
      if (value === "u") return isInfra(row) && row.path_width === null;
      if (value === "ge2") return isInfra(row) && row.path_width !== null && row.path_width >= 2;
      if (value === "lt2") return isInfra(row) && row.path_width !== null && row.path_width < 2;
      if (value === "1s") return row.path_width === 1 && row.multibyte === "yes";
      if (value === "changed") return !!row.width_changes;
      return row.path_width === Number(value);
    case "multi":
      return isInfra(row) && (value === "yes" ? row.multibyte === "yes" : row.multibyte !== "yes");
    case "dup":
      if (value === "uniq") return row.duplicate.value === 0;
      if (value === "shared") return typeof row.duplicate.value === "number" && row.duplicate.value > 0;
      return row.duplicate.color === "red";
    case "clock": {
      const m = clockMagnitude(row);
      if (value === "28h") return (clockStepLevel(row.clock_offset) ?? 0) >= 1;
      if (value === "u") return m === null;
      if (m === null) return false;
      if (value === "unset") return m > CLOCK_NOT_SET_ABS_S;
      if (value === "ok") return m < s.clock_offset_s.yellow_from;
      if (value === "1m") return m >= s.clock_offset_s.yellow_from;
      if (value === "mid") return m >= s.clock_offset_s.yellow_from && m < s.clock_offset_s.red_from && m <= CLOCK_NOT_SET_ABS_S;
      return m >= s.clock_offset_s.red_from;
    }
    case "adv": {
      const h = advertHours(row);
      if (value === "u") return isInfra(row) && h === null;
      if (h === null) return false;
      const a = s.advert_interval_hours;
      const top47 = a.neutral_ceiling_hours ?? 47;
      if (value === "lt12") return h < a.yellow_from;
      if (value === "12") return h >= a.yellow_from && h < a.green_from;
      if (value === "25") return h >= a.green_from && h <= top47;
      return h > top47;
    }
    case "loop": return row.loop_detection.value === value;
    case "pkts": {
      const v = row.pkts_per_hr.value;
      return typeof v === "number" && v >= Number(value);
    }
  }
}

export function activeFilterIds(f: ColumnFilters): ColumnFilterId[] {
  return (Object.keys(f) as ColumnFilterId[]).filter((k) => !!f[k]);
}

export function rowMatchesAllFilters(row: AuditRow, f: ColumnFilters, ctx: FilterContext, top: string | null): boolean {
  return activeFilterIds(f).every((id) => matchesColumnFilter(row, id, f[id] as string, ctx, top));
}

/** The population one summary bar counts: the repeaters and room servers the
 * table would show under Location, Role and every column filter, except the
 * filter that bar itself controls (`leaveOut`), so each bar still shows all
 * of its own segments. */
export function barPopulation(
  rows: AuditRow[], location: string, role: string, f: ColumnFilters, ctx: FilterContext, top: string | null,
  leaveOut: ColumnFilterId[] = [],
): AuditRow[] {
  const rest: ColumnFilters = { ...f };
  for (const id of leaveOut) delete rest[id];
  return populationFor(rows, location, role).filter((r) => isInfra(r) && rowMatchesAllFilters(r, rest, ctx, top));
}

// ---- plain-English counts -----------------------------------------------------

/** "1 node" / "2 nodes": the shared pluraliser for every count in this page. */
export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;
}

/** Turns leftover "time(s)" style shorthand in server text into a real
 * plural ("1 time", "3 times"), so older stored findings read the same as new ones. */
export function fixPluralShorthand(text: string): string {
  return text.replace(/(\d[\d.,]*) ((?:[\w-]+ )*?[\w-]+)\(s\)/g, (_m, n: string, unit: string) =>
    `${n} ${unit}${Number(n.replace(/,/g, "")) === 1 ? "" : "s"}`);
}

export interface CountLineInput {
  /** Rows in the table right now (all filters applied). */
  shown: number;
  /** Rows in the table's population (Location, Role and the companion rule). */
  total: number;
  /** Of those, rows with a red or amber mark. */
  issues: number;
  /** A column filter or a bar segment is active. */
  filtered: boolean;
  showAll: boolean;
  role: string;
}

/** One line, one total: how many of the table's population have an issue / match. */
export function auditCountLine(c: CountLineInput): string {
  const noun = c.showAll ? "node" : c.role === "companion" ? "companion" : null;
  const many = noun === null ? "repeaters and room servers" : `${noun}s`;
  const one = noun === null ? "repeater or room server" : noun;
  const pop = `${c.total.toLocaleString("en-US")} ${c.total === 1 ? one : many}`;
  if (c.filtered) return `${c.shown.toLocaleString("en-US")} of ${pop} ${c.shown === 1 ? "matches" : "match"}`;
  if (c.showAll) return `${c.shown.toLocaleString("en-US")} of ${pop} shown`;
  return `${c.issues.toLocaleString("en-US")} of ${pop} ${c.issues === 1 ? "has" : "have"} an issue`;
}

// ---- dropdown options (plain words; thresholds come from the payload) -------

export interface FilterOption { value: string; label: string }

export function durationLabel(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)} s`;
  const m = Math.round(seconds / 60);
  return `${m} min`;
}

export function hoursLabel(h: number): string {
  return `${Number.isInteger(h) ? h : h.toFixed(1)} h`;
}

export function scopeFilterOptions(top: string | null, tokens: string[]): { main: FilterOption[]; tokens: FilterOption[] } {
  const main: FilterOption[] = [{ value: "", label: "Any" }, { value: "has", label: "Has a scope" }];
  if (top) {
    main.push({ value: "top", label: `Has ${top}` });
    main.push({ value: "noTop", label: `Missing ${top}` });
  }
  main.push({ value: "none", label: "No scope" }, { value: "unknown", label: "Unknown" });
  return { main, tokens: tokens.map((t) => ({ value: t, label: t })) };
}

export const PATH_FILTER_OPTIONS: FilterOption[] = [
  { value: "", label: "Any" },
  { value: "1", label: "1 byte" },
  { value: "1s", label: "1 byte · can switch" },
  { value: "changed", label: "Width changed" },
  { value: "2", label: "2 bytes" },
  { value: "3", label: "3 bytes" },
  { value: "ge2", label: "2+ bytes" },
  { value: "lt2", label: "Under 2 bytes" },
  { value: "u", label: "Unknown" },
];

export const MULTI_FILTER_OPTIONS: FilterOption[] = [
  { value: "", label: "Any" },
  { value: "yes", label: "Seen using 2+ bytes" },
  { value: "unknown", label: "Not seen yet" },
];

export const DUP_FILTER_OPTIONS: FilterOption[] = [
  { value: "", label: "Any" },
  { value: "uniq", label: "Unique" },
  { value: "shared", label: "Shared" },
  { value: "clash", label: "Clash seen" },
];

export function clockFilterOptions(s: AuditColorSettings): FilterOption[] {
  const y = durationLabel(s.clock_offset_s.yellow_from);
  const r = durationLabel(s.clock_offset_s.red_from);
  return [
    { value: "", label: "Any" },
    { value: "ok", label: `Under ${y}` },
    { value: "1m", label: `${y} or more` },
    { value: "mid", label: `${y} to ${r}` },
    { value: "13m", label: `${r} or more` },
    { value: "unset", label: "Clock not set" },
    { value: "28h", label: "28 h step" },
    { value: "u", label: "Unknown" },
  ];
}

export function advertFilterOptions(s: AuditColorSettings): FilterOption[] {
  const a = s.advert_interval_hours;
  const top = a.neutral_ceiling_hours ?? 47;
  return [
    { value: "", label: "Any" },
    { value: "lt12", label: `Under ${hoursLabel(a.yellow_from)}` },
    { value: "12", label: `${a.yellow_from}–${hoursLabel(a.green_from)}` },
    { value: "25", label: `${a.green_from}–${hoursLabel(top)}` },
    { value: "gt47", label: `Over ${hoursLabel(top)}` },
    { value: "u", label: "Unknown" },
  ];
}

export const FOOTPRINT_FILTER_OPTIONS: FilterOption[] = [
  { value: "", label: "Any" },
  { value: "consistent", label: "Consistent" },
  { value: "inconsistent", label: "Inconsistent" },
  { value: "unknown", label: "Unknown" },
];

export const LOOP_FILTER_OPTIONS: FilterOption[] = [
  { value: "", label: "Any" },
  { value: "strict", label: "Strict" },
  { value: "moderate", label: "Moderate" },
  { value: "minimal", label: "Minimal" },
  { value: "off", label: "Off" },
  { value: "unknown", label: "Unknown" },
];

export function pktsFilterOptions(s: AuditColorSettings): FilterOption[] {
  const high = s.pkts_per_hr.chatty_from;
  const low = Math.round(high / 2);
  const opts: FilterOption[] = [{ value: "", label: "Any" }];
  if (low > 0 && low < high) opts.push({ value: String(low), label: `${low} or more` });
  opts.push({ value: String(high), label: `${high} or more` });
  return opts;
}

/** One chip per active filter, whether it was set from a summary bar or a
 *  dropdown, worded like the dropdown: "Path Hash: Under 2 bytes". The chip is
 *  derived from the filter state alone, so clearing the chip or the dropdown
 *  clears both. */
export function filterChips(
  f: ColumnFilters, labels: Record<ColumnFilterId, string>, s: AuditColorSettings, top: string | null, tokens: string[],
): { filter: ColumnFilterId; text: string }[] {
  const scope = scopeFilterOptions(top, tokens);
  const options: Record<ColumnFilterId, FilterOption[]> = {
    scope: [...scope.main, ...scope.tokens],
    foot: FOOTPRINT_FILTER_OPTIONS,
    path: PATH_FILTER_OPTIONS,
    multi: MULTI_FILTER_OPTIONS,
    dup: DUP_FILTER_OPTIONS,
    clock: clockFilterOptions(s),
    adv: advertFilterOptions(s),
    loop: LOOP_FILTER_OPTIONS,
    pkts: pktsFilterOptions(s),
  };
  const order: ColumnFilterId[] = ["scope", "foot", "path", "multi", "dup", "clock", "adv", "loop", "pkts"];
  const out: { filter: ColumnFilterId; text: string }[] = [];
  for (const id of order) {
    const v = f[id];
    if (!v) continue;
    out.push({ filter: id, text: `${labels[id]}: ${options[id].find((o) => o.value === v)?.label ?? v}` });
  }
  return out;
}

/** What the tooltip on a Loop Detection cell says when it reads "unknown". */
export const LOOP_UNKNOWN_TIP = "Never seen relaying in this window";

/** The tooltip on an Advert Interval above the colour range (it has no colour on purpose). */
export function advertNoColourTip(hours: unknown, s: AuditColorSettings): string | null {
  const top = s.advert_interval_hours.neutral_ceiling_hours ?? 47;
  return typeof hours === "number" && hours > top ? `above ${hoursLabel(top)}: no colour by design` : null;
}

// ---- sorting ----------------------------------------------------------------

export type SortKey =
  | "node" | "role" | "region" | "footprint" | "path_hash" | "duplicate" | "clock_offset"
  | "advert_interval" | "loop_detection" | "last_heard" | "pkts_per_hr" | "reliance";

const FOOTPRINT_ORDER: Record<string, number> = { inconsistent: 0, consistent: 1, unknown: 2 };

const LOOP_ORDER: Record<string, number> = { strict: 0, moderate: 1, minimal: 2, off: 3, unknown: 4, "n/a": 5 };

export function sortValue(row: AuditRow, key: SortKey): number | string {
  switch (key) {
    case "node": return row.name.toLowerCase();
    case "role": return row.role;
    case "region": {
      const st = row.region.scope_state;
      if (st === "no_scope") return -1;
      if (st === "unknown" || st === null) return -0.5;
      return scopeTagsOf(row).length;
    }
    case "footprint": return FOOTPRINT_ORDER[footprintValue(row) ?? ""] ?? 3;
    case "path_hash": return row.path_width === null ? 9 : row.path_width;
    case "duplicate": {
      const v = row.duplicate.value;
      if (typeof v !== "number") return -1;
      return v + (row.duplicate.color === "red" ? 100 : 0);
    }
    case "clock_offset": {
      const v = row.clock_offset.value;
      return typeof v === "number" ? v : -Infinity;
    }
    case "advert_interval": return advertHours(row) ?? Infinity;
    case "loop_detection": return LOOP_ORDER[String(row.loop_detection.value)] ?? 9;
    case "last_heard": return row.last_heard ? -Date.parse(row.last_heard) : Infinity;
    case "pkts_per_hr": {
      const v = row.pkts_per_hr.value;
      return typeof v === "number" ? v : -1;
    }
    case "reliance": return row.reliance.value ?? -1;
  }
}

/** Red 0, amber 1, green 2, no colour or unknown 3: lowest = worst. */
export function colorRank(color: string | null | undefined): number {
  return color === "red" ? 0 : color === "yellow" ? 1 : color === "green" ? 2 : 3;
}

const COLOR_COLUMNS: SortKey[] = [
  "region", "path_hash", "duplicate", "clock_offset", "advert_interval", "loop_detection", "pkts_per_hr",
];

/** How bad this row's cell is under one coloured column (0 worst .. 3 none). */
export function sortColor(row: AuditRow, key: SortKey): number {
  switch (key) {
    case "region":
      return row.region.scope_state === "no_scope" ? 0 : colorRank(row.region.color);
    case "footprint": return colorRank(row.footprint?.color);
    case "path_hash": return colorRank(row.path_hash.color);
    case "duplicate": return colorRank(row.duplicate.color);
    case "clock_offset": return colorRank(row.clock_offset.color);
    case "advert_interval": return colorRank(row.advert_interval.color);
    case "loop_detection": return colorRank(row.loop_detection.color);
    case "pkts_per_hr": return colorRank(row.pkts_per_hr.color);
    default: return 3;
  }
}

/** The default order's key: 0 = has a red mark, 1 = amber only, 2 = clean. */
export function severityRank(row: AuditRow): number {
  const worst = Math.min(...COLOR_COLUMNS.map((k) => sortColor(row, k)));
  return worst >= 2 ? 2 : worst;
}

function byName(a: AuditRow, b: AuditRow): number {
  const x = a.name.toLowerCase();
  const y = b.name.toLowerCase();
  return x < y ? -1 : x > y ? 1 : 0;
}

/** `key` null = the default order: issues first (red, then amber), then name.
 * A coloured column sorts worst first when `dir` is 1 and reversed when -1. */
export function sortRows(rows: AuditRow[], key: SortKey | null, dir: 1 | -1): AuditRow[] {
  if (key === null) {
    return [...rows].sort((a, b) => severityRank(a) - severityRank(b) || byName(a, b));
  }
  if (COLOR_COLUMNS.includes(key)) {
    return [...rows].sort((a, b) => dir * (sortColor(a, key) - sortColor(b, key)) || byName(a, b));
  }
  return [...rows].sort((a, b) => {
    const x = sortValue(a, key);
    const y = sortValue(b, key);
    return x < y ? -dir : x > y ? dir : 0;
  });
}

// ---- summary bars -----------------------------------------------------------

export type BarId = "path" | "scopes" | "multibyte" | "top" | "clock";
/** "mid" is the amber band, used only by the Clock Set bar. */
export type BarState = "yes" | "unknown" | "no" | "mid";

export interface BarCounts {
  yes: number;
  unknown: number;
  no: number;
  mid: number;
  total: number;
  confirmed: number;
  inferred: number;
}

/** The bar a row falls in. `topTag` is the scope the top-level bar counts. */
export function barState(row: AuditRow, bar: BarId, topTag: string | null): BarState {
  switch (bar) {
    case "path":
      return row.path_width === null ? "unknown" : row.path_width >= 2 ? "yes" : "no";
    case "scopes": {
      const st = row.region.scope_state;
      return st === "scoped" ? "yes" : st === "no_scope" ? "no" : "unknown";
    }
    case "multibyte":
      return row.multibyte === "yes" ? "yes" : "unknown";
    case "top": {
      if (!topTag) return "unknown";
      if (rowHasScope(row, topTag)) return "yes";
      return row.region.scope_state === "unknown" ? "unknown" : "no";
    }
    case "clock": {
      // The colour the Clock Offset column shows (never recomputed from the number).
      if (clockMagnitude(row) === null) return "unknown";
      const color = clockCellForDisplay(row.clock_offset).color;
      return color === "green" ? "yes" : color === "yellow" ? "mid" : color === "red" ? "no" : "unknown";
    }
  }
}

// ---- bar segments and filter dropdowns share ONE state -----------------------

/** The column filter each summary bar's segments set. */
export const BAR_FILTER: Record<BarId, ColumnFilterId> = { path: "path", scopes: "scope", multibyte: "multi", top: "scope", clock: "clock" };

/** The filter dropdown value a bar segment stands for. */
export function barFilterValue(bar: BarId, state: BarState): string {
  switch (bar) {
    case "path": return state === "yes" ? "ge2" : state === "no" ? "lt2" : "u";
    case "scopes": return state === "yes" ? "has" : state === "no" ? "none" : "unknown";
    case "multibyte": return state === "yes" ? "yes" : "unknown";
    case "top": return state === "yes" ? "top" : state === "no" ? "noTop" : "unknown";
    case "clock": return state === "yes" ? "ok" : state === "mid" ? "mid" : state === "no" ? "13m" : "u";
  }
}

const BAR_STATES: BarState[] = ["yes", "mid", "unknown", "no"];

/** Which segment of `bar` the current filters select, or null. */
export function barSelection(bar: BarId, f: ColumnFilters): BarState | null {
  const cur = f[BAR_FILTER[bar]];
  if (!cur) return null;
  return BAR_STATES.find((s) => barFilterValue(bar, s) === cur) ?? null;
}

/** The segments the active filters select, one per filter (a chip each). */
export function activeBarSegments(f: ColumnFilters): { bar: BarId; state: BarState; filter: ColumnFilterId }[] {
  const out: { bar: BarId; state: BarState; filter: ColumnFilterId }[] = [];
  for (const bar of ["path", "scopes", "top", "multibyte", "clock"] as BarId[]) {
    const state = barSelection(bar, f);
    if (state && !out.some((o) => o.filter === BAR_FILTER[bar])) out.push({ bar, state, filter: BAR_FILTER[bar] });
  }
  return out;
}

/** Clicking a segment sets its dropdown; clicking it again clears it. */
export function toggleBarFilter(f: ColumnFilters, bar: BarId, state: BarState): ColumnFilters {
  const id = BAR_FILTER[bar];
  const value = barFilterValue(bar, state);
  return { ...f, [id]: f[id] === value ? "" : value };
}

function scopeKind(row: AuditRow, tag: string | null): "confirmed" | "inferred" | null {
  const pills = (row.region.scope_pills ?? []).filter((p) => p.color !== "red");
  const hit = tag ? pills.filter((p) => p.tag.toLowerCase() === tag.toLowerCase()) : pills;
  if (hit.length === 0) return null;
  return hit.some((p) => p.confirmed) ? "confirmed" : "inferred";
}

export function barCounts(rows: AuditRow[], bar: BarId, topTag: string | null): BarCounts {
  const out: BarCounts = { yes: 0, unknown: 0, no: 0, mid: 0, total: 0, confirmed: 0, inferred: 0 };
  for (const r of rows) {
    if (!isInfra(r)) continue;
    out.total += 1;
    out[barState(r, bar, topTag)] += 1;
    if (bar === "scopes" || bar === "top") {
      const k = scopeKind(r, bar === "top" ? topTag : null);
      if (k) out[k] += 1;
    }
  }
  return out;
}

export function barPercent(c: BarCounts): number {
  return c.total > 0 ? Math.round((c.yes * 100) / c.total) : 0;
}

/** Share of the nodes with a known state (total minus unknown) that are in the good state; 0 when none is known. */
export function barPercentOfKnown(c: BarCounts): number {
  const known = c.total - c.unknown;
  return known > 0 ? Math.round((c.yes * 100) / known) : 0;
}

/** "12% of all · 43% of known": the Clock Set headline. */
export function clockSetHeadline(c: BarCounts): string {
  return `${barPercent(c)}% of all \u00b7 ${barPercentOfKnown(c)}% of known`;
}

export function barRightText(c: BarCounts, bar?: BarId): string {
  const unk = c.unknown > 0 ? `, ${c.unknown.toLocaleString("en-US")} unknown` : "";
  const head = bar === "clock" ? clockSetHeadline(c) : `${barPercent(c)}%`;
  return `${head} (${c.yes.toLocaleString("en-US")} of ${c.total.toLocaleString("en-US")}${unk})`;
}

function sharePct(n: number, total: number): string {
  return `${((n * 100) / total).toFixed(1)}%`;
}

/** The second Path Hash bar figure, counted the same way as the bar: "whole mesh: 76.1% of relayed packets carry 2+ byte hashes". Null when no packet was counted. */
export function packetsByWidthText(p: AuditPathHashPackets | undefined | null): string | null {
  if (!p || p.total <= 0) return null;
  return `whole mesh: ${sharePct(p.w2 + p.w3, p.total)} of relayed packets carry 2+ byte hashes`;
}

/** Hover text for that figure, with the three shares. */
export function packetsByWidthTip(p: AuditPathHashPackets): string {
  const n = (v: number) => v.toLocaleString("en-US");
  const part = (label: string, v: number) => `${label}: ${sharePct(v, p.total)} (${n(v)})`;
  return `Share of relayed packets in this window whose path hashes are 1, 2 or 3 bytes; counts every packet that carried at least one hop. ${part("1 byte", p.w1)}; ${part("2 bytes", p.w2)}; ${part("3 bytes", p.w3)}; ${n(p.total)} packets in all.`;
}

/** "1 → 2 → 1" */
export function widthSequenceText(widths: number[]): string {
  return widths.join(" → ");
}

/** "12.3 km", or the dash when either node has no position. */
export function partnerKmText(km: number | null): string {
  return km === null ? DASH : `${km.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} km`;
}

/** Hover text for a Heard together value. */
export function heardTogetherTip(p: AuditDuplicatePartner): string {
  if (p.heard_together === null) return "Fewer than two of the pair were heard in this window";
  return p.heard_together
    ? "An observer heard adverts from both in this window"
    : "No observer heard adverts from both in this window";
}

/** "Yes (A, B)" / "No" / dash. */
export function heardTogetherText(p: AuditDuplicatePartner): string {
  if (p.heard_together === null) return DASH;
  if (!p.heard_together) return "No";
  return p.observers.length > 0 ? `Yes (${p.observers.join(", ")})` : "Yes";
}

export function barLabel(bar: BarId, topTag: string | null): string {
  switch (bar) {
    case "path": return "Path Hash 2+ Bytes";
    case "scopes": return "Region Scopes";
    case "multibyte": return "Multibyte Firmware Support";
    case "top": return "Top-Level Region"; // the scope is shown once, by the picker (and in the Rings and Gauges count line)
    case "clock": return "Clock Set";
  }
}

const BAR_UNKNOWN_NOTE =
  ' "Unknown" here means that column has no data yet for those nodes, not that their role is unknown. The population is repeaters and room servers confirmed by their own adverts.';

export function barTooltip(bar: BarId, c: BarCounts, topTag: string | null): string {
  if (bar === "clock") return barTooltipBase(bar, c, topTag);
  return barTooltipBase(bar, c, topTag) + BAR_UNKNOWN_NOTE;
}

function barTooltipBase(bar: BarId, c: BarCounts, topTag: string | null): string {
  const scopeNow = `${c.confirmed.toLocaleString("en-US")} confirmed, ${c.inferred.toLocaleString("en-US")} inferred.`;
  switch (bar) {
    case "path":
      return "Repeaters and room servers whose newest own advert in the last 7 days used a 2 or 3-byte path hash. Unknown means no usable advert was seen in that time.";
    case "scopes":
      return `Repeaters and room servers seen with at least one region scope in this window. Confirmed: the node reports that scope, or itself sent a scoped packet with it. Inferred: it was only seen relaying packets with that scope. Unknown: nothing from this node was seen in this window, so there is no scope to judge. Right now ${scopeNow}`;
    case "multibyte":
      return "Repeaters and room servers we have seen use a 2 or 3-byte path hash, either in their own advert or by relaying in a multi-byte path. Unknown means we have not seen that yet, which does not mean the firmware cannot do it.";
    case "top":
      return `Repeaters and room servers carrying the ${topTag ?? "top-level"} scope in this window. Confirmed: the node reports it, or itself sent a packet with it. Inferred: only seen relaying it. No: seen with other scopes but not this one. Unknown: nothing from this node was seen in this window. Right now ${scopeNow} The preset is set in Settings > Regions.`;
    case "clock":
      return `Repeaters and room servers whose clock is within 1 minute (green) or 13 minutes (yellow) of real time, from their own advert timestamps; red = more than 13 minutes or never set. ${c.unknown.toLocaleString("en-US")} of ${c.total.toLocaleString("en-US")} give no clock reading.`;
  }
}

export function barSegmentLabel(state: BarState): string {
  return state === "yes" ? "yes" : state === "no" ? "no" : state === "mid" ? "amber" : "unknown";
}

// ---- header tooltips ---------------------------------------------------------

export function headerTooltip(col: SortKey, s: AuditColorSettings, fixedLookbackDays: number | null, widthLookbackDays = 7): string {
  const days = fixedLookbackDays ?? 6;
  const y = durationLabel(s.clock_offset_s.yellow_from);
  const r = durationLabel(s.clock_offset_s.red_from);
  const a = s.advert_interval_hours;
  switch (col) {
    case "node": return "The node's name. Click it to open its page. Unless you sort by another column, the table lists nodes with a red mark first, then amber, then the rest, each group by name.";
    case "role": return "Repeater, room server or companion.";
    case "region":
      return "Region scopes this node was seen with in this window. Solid: the node reports that scope, or it sent a scoped packet with it. Dashed: it was only seen relaying that scope. Red No scope: it reports no real scope, or it was active but every packet it sent or relayed was unscoped. A dash: nothing seen in this window.";
    case "footprint": return FOOTPRINT_HEADER_TIP;
    case "path_hash":
      return `The path hash width on the node's newest own advert in the last ${widthLookbackDays} days. Green for 2 or 3 bytes, red for 1 byte. Can switch: a 1-byte node we have also seen using a 2 or 3-byte path hash, so its firmware supports it and only its setting needs changing.`;
    case "duplicate":
      return "Compares this node's short ID, at its own path hash width, with other repeaters and room servers heard in this window. Amber: it shares that ID with at least one of them. Red: this node is itself in a pair where a repeater was seen relaying next to both, so the clash is a real routing risk. Nearest = closest partner with a known position.";
    case "clock_offset":
      return `How far the node's clock is from real time, from its last ${days} days of adverts. Green under ${y}, amber ${y} or more, red ${r} or more.`;
    case "advert_interval":
      return `Most common gap between this node's flood adverts heard in the last ${days} days. Repeater and room-server gaps under 3 h are ignored; gaps are grouped by the hour. Configured policy: green from ${a.green_from} h, amber ${a.yellow_from}–${a.green_from} h, red under ${a.yellow_from} h. Configured neutral ceiling: ${a.neutral_ceiling_hours ?? 47} h; above it has no colour. The default 47 h ceiling matches MeshCore's new-install reference.`;
    case "loop_detection":
      return "The strictest Loop Detection setting still consistent with what we have seen. Worked out from packets the node forwarded even though its own ID was already in the path, so it can show stricter than the node really is when no loops have happened near it yet, but never looser. Green strict, amber moderate, red minimal or off. Unknown: never seen relaying in this window.";
    case "last_heard": return "When we last heard anything from this node.";
    case "pkts_per_hr":
      return `Packets this node sent that we can tie to it, per hour over this window. Red at ${s.pkts_per_hr.chatty_from} or more.`;
    case "reliance":
      return "Of the packets that went through this node and were heard by 2 or more observers, the share where every observed path went through it (no way around it). Shown for information only, with no colour.";
  }
}

export const SORT_COLUMNS: { key: SortKey; label: string }[] = [
  { key: "node", label: "Node" },
  { key: "role", label: "Role" },
  { key: "region", label: "Region" },
  { key: "footprint", label: "Footprint" },
  { key: "path_hash", label: "Path Hash" },
  { key: "duplicate", label: "Duplicate" },
  { key: "clock_offset", label: "Clock Offset" },
  { key: "advert_interval", label: "Advert Interval" },
  { key: "loop_detection", label: "Loop Detection" },
  { key: "last_heard", label: "Last Heard" },
  { key: "pkts_per_hr", label: "Pkts/hr" },
  { key: "reliance", label: "Reliance" },
];

/** The phone card shows every column except Node (the card title) as a "Label  value" line, in table order. */
export function cardFields(): { key: SortKey; label: string }[] {
  return SORT_COLUMNS.filter((c) => c.key !== "node");
}

/** The phone Sort dropdown's value for a sort key; "" is the default order (issues first). */
export function sortChoiceValue(key: SortKey | null): string {
  return key ?? "";
}

/** Inverse of sortChoiceValue; anything that is not a real column is the default order. */
export function parseSortChoice(value: string): SortKey | null {
  return SORT_COLUMNS.find((c) => c.key === value)?.key ?? null;
}

export function columnLabel(key: SortKey): string {
  return SORT_COLUMNS.find((c) => c.key === key)?.label ?? key;
}

// ---- which rows the table shows ----------------------------------------------

export interface ViewState {
  location: string;
  role: string;
  filters: ColumnFilters;
  showAll: boolean;
}

export function visibleRows(
  rows: AuditRow[], v: ViewState, ctx: FilterContext, top: string | null,
): AuditRow[] {
  const pop = issuePopulation(rows, v.location, v.role, v.showAll);
  const anyFilter = activeFilterIds(v.filters).length > 0;
  return pop.filter((r) => rowMatchesAllFilters(r, v.filters, ctx, top) && (anyFilter || v.showAll || r.has_issue));
}

/** Candidate nodes for a finding-level worklist. Applies the same role,
 * location, and column predicates as visibleRows, but deliberately omits
 * visibleRows' default `has_issue` gate so a reported finding can surface
 * its node even when the Audit table's own issue flag is false. */
export function findingWorklistRows(
  rows: AuditRow[], v: ViewState, ctx: FilterContext, top: string | null,
): AuditRow[] {
  const pop = issuePopulation(rows, v.location, v.role, v.showAll);
  return pop.filter((r) => rowMatchesAllFilters(r, v.filters, ctx, top));
}

/** The top-level bar's selected scope: the Settings preset, else the scope
 * carried by the most repeaters and room servers; null when there are none (the bar is then hidden). */
export function defaultTopTag(top: string | null, tokens: string[], rows: AuditRow[] = []): string | null {
  if (top) return top;
  let best: string | null = null;
  let bestN = -1;
  for (const t of tokens) {
    const n = rows.filter((r) => isInfra(r) && rowHasScope(r, t)).length;
    if (n > bestN) { best = t; bestN = n; }
  }
  return best;
}

export function locationOptions(rows: AuditRow[]): FilterOption[] {
  const seen = new Map<string, string>();
  for (const r of rows) {
    const v = r.region.value;
    if (typeof v === "string" && v && !seen.has(v)) seen.set(v, r.region.label ?? v);
  }
  return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1])).map(([value, label]) => ({ value, label }));
}

// ---- what a dash means -----------------------------------------------------------

export type DashColumn =
  | "region" | "footprint" | "path_hash" | "duplicate" | "clock_offset" | "advert_interval"
  | "loop_detection" | "last_heard" | "pkts_per_hr" | "reliance";

/** Why a cell shows a dash: not applicable to this role, not enough data, or none. */
export function dashTip(row: AuditRow, col: DashColumn): string {
  const infra = isInfra(row);
  switch (col) {
    case "path_hash":
    case "loop_detection":
      return infra
        ? "Not enough data: nothing usable from this node was seen in this window."
        : "Not applicable: companions do not relay, so they never appear in a path.";
    case "duplicate":
      if (!infra) return "Not applicable: companions never appear in a path, so a shared short ID does not matter.";
      return row.duplicate.value === 0
        ? "None: no other repeater or room server shares this node's short ID."
        : "Not enough data to compare this node's short ID.";
    case "reliance":
      return infra
        ? "not enough packets"
        : "Not applicable: companions do not relay.";
    case "region": return "Nothing seen from this node in this window, so it has no region scope.";
    case "footprint": return infra ? "Not enough data" : "Not applicable";
    case "last_heard": return "Never heard from.";
    case "clock_offset": return "Not enough data: too few adverts with a usable clock were seen.";
    case "advert_interval":
      return infra ? "Not enough data: too few adverts were seen to work out an interval." : "Not applicable to this role.";
    case "pkts_per_hr": return "Not enough data: no packets that we can tie to this node were seen.";
  }
}

// ---- node page findings: honest titles and display names ---------------------------

const FINDING_TOPIC: Record<string, string> = {
  d1_advert_rate: "Advert Interval",
  d2_boot_loop: "Clock resets",
  d3_loop_detect: "Loop Detection",
  d4_path_hash_width: "Path hash",
  d5_prefix_collision: "Shared short ID",
  d8_position_plausibility: "Location",
};

/** The title a finding gets on a node page: it carries the same value the
 * table column shows (Loop Detection), and a finding with no proof behind
 * it is titled "<topic>: not enough data", never as a confirmed problem. */
export function findingTitle(
  detector: string, apiLabel: string | null | undefined, verdict: string, loopValue: string | null,
): string {
  const topic = FINDING_TOPIC[detector];
  if (verdict === "INSUFFICIENT_EVIDENCE") return `${topic ?? apiLabel ?? detector}: not enough data`;
  // The API detector label describes power-up, but a finding can also report
  // an unconfirmed backward clock move. Keep the headline neutral; the
  // finding summary explains when the firmware reset signature confirms reboot.
  if (detector === "d2_boot_loop") return "Clock resets";
  if (detector === "d3_loop_detect") {
    const known = ["strict", "moderate", "minimal", "off"];
    return loopValue && known.includes(loopValue) ? `Loop Detection ${loopValue}` : "Loop Detection";
  }
  return apiLabel || topic || detector;
}

const CAVEAT_MARK = "This is a diagnostic signal, never a confirmed claim";

/** Splits a finding's text into a one-line lead, the possible causes as a list,
 * and the caveat sentence (shown behind a "details" toggle). Text without the
 * caveat comes back unchanged as the lead. */
export function splitFinding(text: string): { lead: string; causes: string[]; caveat: string | null } {
  const at = text.indexOf(CAVEAT_MARK);
  if (at < 0) return { lead: text, causes: [], caveat: null };
  const lead = text.slice(0, at).trim();
  const rest = text.slice(at + CAVEAT_MARK.length).replace(/^[\s.\u2014\u2013-]+/, "");
  const causes = rest.split(/(?<=\.)\s+(?=[A-Z])/).map((x) => x.trim()).filter(Boolean);
  return { lead, causes, caveat: `${CAVEAT_MARK}.` };
}

/** A finding's text for the node page: one plain sentence up front, the technical
 *  wording (and anything after the first sentence) behind the "details" toggle.
 *  Only rephrases what the finding already says. */
export function plainFinding(
  detector: string, verdict: string, text: string,
): { lead: string; causes: string[]; caveat: string | null } {
  if (detector === "d3_loop_detect") {
    return {
      lead: "This repeater passed on a packet that already had its own ID in the path more often than a stricter setting allows.",
      causes: [], caveat: text,
    };
  }
  if (detector === "d1_advert_rate" && verdict === "INSUFFICIENT_EVIDENCE") {
    return { lead: "Too few adverts were seen from this node in this window to work out how often it sends them.", causes: [], caveat: text };
  }
  const split = splitFinding(text);
  if (split.caveat !== null) return split;
  const sentences = text.split(/(?<=[.)])\s+(?=[A-Z(])/).map((x) => x.trim()).filter(Boolean);
  if (sentences.length < 2) return split;
  return { lead: sentences[0], causes: [], caveat: sentences.slice(1).join(" ") };
}

function escapeRe(x: string): string {
  return x.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Swaps internal source and transport ids in finding text for display
 * names (longest id first, whole words only), then fixes "time(s)" shorthand. */
/** Display names for the internal ids that show up in finding text: the
 * CoreScope feed, our own radio (any of our transports, whatever the radio
 * is called in settings) and every other observer by its label. */
export function observerDisplayNames(observers: { id: string; label?: string; kind?: string }[]): Record<string, string> {
  const names: Record<string, string> = { corescope: "CoreScope", meshcore_vnode: "our radio", meshtastic_vnode: "our radio" };
  for (const o of observers) {
    if (o.id === "corescope") continue;
    if (o.kind === "transport") names[o.id] = "our radio";
    else if (o.label && o.label !== o.id) names[o.id] = o.label;
  }
  return names;
}

export function displayNames(text: string, names: Record<string, string>): string {
  let out = text;
  for (const id of Object.keys(names).sort((a, b) => b.length - a.length)) {
    out = out.replace(new RegExp(`(?<![\\w-])${escapeRe(id)}(?![\\w-])`, "g"), () => names[id]);
  }
  return fixPluralShorthand(out);
}

// --- Airtime block (node detail) --------------------------------------------------

/** Seconds per day, one decimal, thousands separators ("1,240.5"). */
export function fmtSecPerDay(v: number): string {
  return (Math.round(v * 10) / 10).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/** "Advert 31.0 s, Direct message 4.2 s" -- the per-type split; `name` turns a raw type into plain words. */
export function airtimeTypeSplit(by: AuditAirtimeType[], name: (t: string) => string): string {
  return by.map((t) => `${name(t.type)} ${fmtSecPerDay(t.s_per_day)} s`).join(", ");
}

/** Hover text on a rank tag. */
export const AIRTIME_RANK_TIP = "Rank among repeaters and room servers by seconds per day in this window, 1 = most";

/** "rank 12 of 914 nodes", or null when the node has no rank (older snapshot, or not a repeater or room server). */
export function airtimeRankText(b: { rank?: number | null; of?: number | null }): string | null {
  return typeof b.rank === "number" && typeof b.of === "number" ? `rank ${b.rank} of ${b.of} ${b.of === 1 ? "node" : "nodes"}` : null;
}

export const AIRTIME_ADVERT_TIP =
  "Zero-hop adverts are never repeated. Airtime and repeats are for flood adverts only: the airtime of one flood advert counts the node's own send plus every repeat of it by other repeaters that an observer heard, averaged over the flood adverts.";

/** The one phrasing of the advert gap: "most common gap 5.0 h (8 samples)"; the count is left out when unknown. */
export function advertGapText(intervalLabel: string, sampleCount: number | null | undefined): string {
  const n = typeof sampleCount === "number" ? ` (${sampleCount} ${sampleCount === 1 ? "sample" : "samples"})` : "";
  return `most common gap ${intervalLabel}${n}`;
}

/** The same gap as a sentence in the opened row: "Most common gap 5.0 h (8 samples)." */
export function advertGapSentence(intervalLabel: string, sampleCount: number | null | undefined): string {
  const t = advertGapText(intervalLabel, sampleCount);
  return `${t.charAt(0).toUpperCase()}${t.slice(1)}.`;
}

/** Inline advert values; airtime per flood advert includes its own send and observed repeats. */
export function airtimeAdvertLine(a: AuditAirtime["advert"], gapText: string | null | undefined, windowLabel: string): string | null {
  if (!a) return null;
  const flood = a.flood.toLocaleString("en-US");
  const zeroHop = a.zero_hop.toLocaleString("en-US");
  const airtime = a.flood === 0
    ? "—"
    : a.s_per_advert == null
      ? "Unknown"
      : `${fmtSecPerDay(a.s_per_advert)} s (own send + observed repeats)`;
  const repeats = a.flood === 0
    ? "—"
    : a.repeats_per_advert == null
      ? "Unknown"
      : fmtSecPerDay(a.repeats_per_advert).replace(/\.0$/, "");
  const values = [
    `Window: ${windowLabel}`,
    `Flood adverts: ${flood}`,
    `Zero-hop adverts: ${zeroHop}`,
    `Avg airtime / flood advert: ${airtime}`,
    `Avg observed repeats / advert: ${repeats}`,
  ];
  if (gapText) values.push(gapText);
  return values.join(" · ");
}

/** The Duplicate cell on one short line: "Shared ×2 · 309 mi". `distanceBoth` is "309 mi (497 km)"; only the first figure shows. */
export function duplicateCellText(count: number | null, distanceBoth: string, fallbackLabel: string | null | undefined): string {
  const short = distanceBoth.replace(/\s*\(.*\)\s*$/, "");
  const head = count != null && count > 0 ? `Shared \u00d7${count}` : (fallbackLabel ?? DASH);
  return `${head} \u00b7 ${short}`;
}

/** The hover on that cell: the full label, both units, and what "nearest" means. */
export function duplicateCellTip(count: number | null, distanceBoth: string, label: string | null | undefined): string {
  const who = label && label !== DASH ? `${label[0].toUpperCase()}${label.slice(1)}. ` : count != null && count > 0 ? `Shared with ${count}. ` : "";
  return `${who}Nearest partner with a known position: ${distanceBoth}.`;
}

const AIRTIME_CAVEAT = "Only transmissions an observer heard are counted, so the real figure can be higher.";
export const AIRTIME_SENT_TIP =
  `Seconds per day this node's own packets spent on air when it sent them, worked out from each packet's real size and the radio settings. ${AIRTIME_CAVEAT}`;
export const AIRTIME_RELAYED_TIP =
  `Seconds per day this node spent on air repeating other nodes' packets. ${AIRTIME_CAVEAT}`;
export const AIRTIME_CAUSED_TIP =
  `Seconds per day of air that this node's own packets used across the whole mesh: its own transmission plus every repeat by other repeaters. ${AIRTIME_CAVEAT}`;

/** Why a node has no airtime block. */
export const AIRTIME_NONE_REASON =
  "No transmission of this node or by this node was heard by an observer in this window, so there is no airtime to count.";


/** How many filters are active: the nine column filters plus Location and Role. */
export function activeFilterCount(f: ColumnFilters, location: string, role: string): number {
  return Object.values(f).filter(Boolean).length + (location ? 1 : 0) + (role ? 1 : 0);
}

function isDashCell(c: AuditCell): boolean {
  return (c.label ?? DASH) === DASH;
}

/** True when the column's value would render as the dash placeholder. */
export function cardValueIsDash(row: AuditRow, key: SortKey): boolean {
  switch (key) {
    case "region": return !row.region.scope_pills || row.region.scope_pills.length === 0;
    case "footprint": return !isInfra(row) || row.footprint === undefined || isDashCell(row.footprint);
    case "path_hash": return isDashCell(row.path_hash);
    case "duplicate": return isDashCell(row.duplicate) && row.duplicates_nearest_km == null;
    case "clock_offset": return isDashCell(clockCellForDisplay(row.clock_offset));
    case "advert_interval": return isDashCell(row.advert_interval);
    case "loop_detection": return isDashCell(row.loop_detection);
    case "last_heard": return !row.last_heard;
    case "pkts_per_hr": return isDashCell(row.pkts_per_hr);
    case "reliance": return !(row.role === "repeater" || row.role === "room_server") || row.reliance.value === null;
    default: return false;
  }
}

/** The phone card lists only the columns whose value is not the dash placeholder. */
export function cardFieldsFor(row: AuditRow): { key: SortKey; label: string }[] {
  return cardFields().filter((f) => !cardValueIsDash(row, f.key));
}

// ---- scope footprint ----------------------------------------------------------

export interface AuditFootprintScope {
  scope: string;
  packets: number;
  days: number;
  declared: boolean;
  /** "below_min_share": an undeclared scope tolerated under the minimum share; not counted. */
  tag?: string | null;
}

export interface AuditFootprint {
  label: "consistent" | "inconsistent" | "unknown";
  /** Why a verdict is Unknown: no_declaration or nothing_steady. */
  reason?: string;
  declared: string[];
  /** Declared scopes (as in `declared`) that are stale: still counted, marked in the detail. */
  stale_declared?: Record<string, ScopeStaleInfo>;
  scopes: AuditFootprintScope[];
  within: number;
  relayed: number;
  off_share_pct: number | null;
}

export const FOOTPRINT_LABELS: Record<string, string> = {
  consistent: "Consistent", inconsistent: "Inconsistent", unknown: "Unknown",
};

export const FOOTPRINT_HEADER_TIP =
  "Declared Of Relayed. Settings: audit.backbone_stable_min_days, audit.backbone_stable_min_transmissions, audit.footprint_min_off_share_pct";

export const FOOTPRINT_COLUMN_HEADINGS = ["Scope", "Packets", "Days", "Declared"] as const;

/** The footprint verdict for a repeater or room server: consistent, inconsistent or unknown; null when the row has none. */
export function footprintValue(row: AuditRow): string | null {
  const v = row.footprint?.value;
  return typeof v === "string" ? v : null;
}

/** The structured footprint evidence, or null for an older snapshot, a companion, or a row with nothing to compare. */
export function footprintOf(row: AuditRow): AuditFootprint | null {
  const ev = row.footprint?.evidence as Partial<AuditFootprint> | undefined;
  if (!ev || !Array.isArray(ev.scopes) || !Array.isArray(ev.declared)) return null;
  return ev as AuditFootprint;
}

export function footprintLabelText(row: AuditRow): string {
  return FOOTPRINT_LABELS[footprintValue(row) ?? ""] ?? DASH;
}

/** Declared scopes as one line, "None" when the node reported none. */
export function footprintDeclaredText(fp: AuditFootprint): string {
  return fp.declared.length > 0
    ? fp.declared.map((d) => (fp.stale_declared?.[d] ? `${d} (${scopeStaleLabel(fp.stale_declared[d].stale_days)})` : d)).join("  ")
    : "None";
}

/** Steady relayed scopes as one line, "None" when nothing was steady. */
export function footprintRelayedText(fp: AuditFootprint): string {
  return fp.scopes.length > 0 ? fp.scopes.map((s) => s.scope).join("  ") : "None";
}

/** "Off Share 26%", or null when there is nothing relayed or nothing off. */
export function footprintOffShareText(fp: AuditFootprint): string | null {
  if (fp.off_share_pct === null || fp.off_share_pct === undefined || fp.scopes.every((s) => s.declared)) return null;
  return `Off Share ${Math.round(fp.off_share_pct)}%`;
}

export const FOOTPRINT_SCOPE_TAGS: Record<string, string> = { below_min_share: "Below Min Share" };
export const FOOTPRINT_UNKNOWN_REASONS: Record<string, string> = { no_declaration: "No Declaration", nothing_steady: "Nothing Steady" };

/** Short tag on a detail row for a tolerated scope, else null. */
export function footprintScopeTag(sc: AuditFootprintScope): string | null {
  return sc.tag ? FOOTPRINT_SCOPE_TAGS[sc.tag] ?? null : null;
}

/** Short tag for an Unknown verdict's reason, else null. */
export function footprintReasonTag(fp: AuditFootprint): string | null {
  return fp.label === "unknown" && fp.reason ? FOOTPRINT_UNKNOWN_REASONS[fp.reason] ?? null : null;
}
